import React, { useState, useEffect } from 'react';
import { Tabs, Spin, Empty, message, Alert, Card } from 'antd';
import { getSidebarInfo, SidebarCaseInfo } from '../../../services/wechatSidebar';
import { getCaseById, Case } from '../../../services/case';
import CaseFollowUpTab from '../../Case/components/CaseDetail/CaseFollowUpTab';
import CaseSelector from './CaseSelector';
import CaseBasicInfoMobile from './CaseBasicInfoMobile';
import './styles.css';

const CaseManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [caseLoading, setCaseLoading] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [cases, setCases] = useState<SidebarCaseInfo[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [activeTab, setActiveTab] = useState('followup');
  const [error, setError] = useState<string | null>(null);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [noCases, setNoCases] = useState(false); // 标识群聊无关联案件

  // 初始化企业微信 JS-SDK
  const initWxSDK = async () => {
    try {
      // 检查是否在企业微信环境中
      const isWeChat = /wxwork/i.test(navigator.userAgent);
      if (!isWeChat) {
        console.log('非企业微信环境，跳过 JS-SDK 初始化');
        // 直接标记为已初始化，让后续流程继续
        setSdkInitialized(true);
        return;
      }

      const currentUrl = window.location.href.split('#')[0];
      
      // 获取 JS-SDK 配置（企业级）
      const { post } = await import('../../../utils/request');
      const config = await post<{
        corpId: string;
        timestamp: number;
        nonceStr: string;
        signature: string;
      }>('/wechat/jssdk/config', {
        url: currentUrl
      });
      
      // 获取应用级 JS-SDK 配置（agentConfig）
      const agentConfig = await post<{
        corpId: string;
        agentId: string;
        timestamp: number;
        nonceStr: string;
        signature: string;
      }>('/wechat/jssdk/agent-config', {
        url: currentUrl
      });
      
      // 配置 JS-SDK
      if (window.wx) {
        // 1. 先配置企业级 config
        window.wx.config({
          beta: true,
          debug: false,
          appId: config.corpId,
          timestamp: config.timestamp,
          nonceStr: config.nonceStr,
          signature: config.signature,
          jsApiList: [],
        });
        
        window.wx.ready(() => {
          // 2. 再配置应用级 agentConfig
          window.wx.agentConfig({
            corpid: agentConfig.corpId,
            agentid: agentConfig.agentId,
            timestamp: agentConfig.timestamp,
            nonceStr: agentConfig.nonceStr,
            signature: agentConfig.signature,
            jsApiList: ['getCurExternalChat', 'openEnterpriseChat'],
            success: () => {
              setSdkInitialized(true);
            },
            fail: (res: any) => {
              console.error('应用级 JS-SDK 配置失败:', res);
              // 所有错误都显示为"未关联案件"
              setNoCases(true);
              setLoading(false);
            }
          });
        });
        
        window.wx.error((res: any) => {
          console.error('企业级 JS-SDK 配置失败:', res);
        });
      } else {
        // 所有错误都显示为"未关联案件"
        setNoCases(true);
        setLoading(false);
      }
    } catch (error: any) {
      console.error('初始化 JS-SDK 失败:', error);
      // 如果是本地测试环境，不阻止继续执行
      const params = new URLSearchParams(window.location.search);
      const gid = params.get('group_id') || '';
      if (gid && gid !== '$CHAT_ID$' && gid !== '%24CHAT_ID%24') {
        console.log('检测到 URL 参数，跳过 JS-SDK 初始化');
        setSdkInitialized(true);
      } else {
        // 所有错误都显示为"未关联案件"
        setNoCases(true);
        setLoading(false);
      }
    }
  };

  // 通过 JS-SDK 获取群聊ID
  const getGroupIdFromSDK = () => {
    return new Promise<string>((resolve, reject) => {
      if (!window.wx) {
        reject(new Error('企业微信 JS-SDK 未加载'));
        return;
      }
      
      window.wx.invoke('getCurExternalChat', {}, (res: any) => {
        if (res.err_msg === 'getCurExternalChat:ok') {
          resolve(res.chatId);
        } else {
          reject(new Error('获取群聊ID失败: ' + res.err_msg));
        }
      });
    });
  };

  // 获取 group_id 从 URL 或企业微信 JS-SDK
  const getGroupId = async (): Promise<string> => {
    const params = new URLSearchParams(window.location.search);
    let gid = params.get('group_id') || '';
    
    // 如果 URL 中有有效的 group_id（不是变量），直接使用
    if (gid && gid !== '$CHAT_ID$' && gid !== '%24CHAT_ID%24') {
      console.log('✅ 从 URL 获取 group_id:', gid);
      return gid;
    }
    
    // URL 参数无效，尝试通过 JS-SDK 获取
    try {
      gid = await getGroupIdFromSDK();
      console.log('✅ 通过 JS-SDK 获取 group_id:', gid);
      return gid;
    } catch (error: any) {
      console.error('❌ 通过 JS-SDK 获取失败:', error);
      // 所有错误都返回空字符串，统一显示"未关联案件"
      return '';
    }
  };

  // 初始化：加载企业微信 JS-SDK 并获取 group_id
  useEffect(() => {
    const init = async () => {
      try {
        // 1. 初始化 JS-SDK
        await initWxSDK();
      } catch (error: any) {
        console.error('初始化失败:', error);
        setError('初始化失败: ' + error.message);
        setLoading(false);
      }
    };
    
    init();
  }, []);

  // 当 SDK 初始化完成后，获取群聊ID
  useEffect(() => {
    if (!sdkInitialized) return;
    
    const fetchGroupId = async () => {
      try {
        const gid = await getGroupId();
        
        if (!gid) {
          // 无法获取群聊ID，显示"未关联案件"
          setNoCases(true);
          setLoading(false);
          return;
        }
        
        setGroupId(gid);
      } catch (error: any) {
        console.error('获取群聊ID失败:', error);
        // 所有错误都显示"未关联案件"
        setNoCases(true);
        setLoading(false);
      }
    };
    
    fetchGroupId();
  }, [sdkInitialized]);

  // 加载群聊关联的案件列表
  useEffect(() => {
    if (groupId) {
      loadCases();
    }
  }, [groupId]);

  // 加载案件详情
  useEffect(() => {
    if (selectedCaseId) {
      loadCaseDetail();
    }
  }, [selectedCaseId]);

  const loadCases = async () => {
    setLoading(true);
    setError(null);
    setNoCases(false);
    
    try {
      const data = await getSidebarInfo(groupId);
      setCases(data.cases);
      
      // 自动选中第一个案件
      if (data.cases.length > 0) {
        setSelectedCaseId(data.cases[0].id);
      } else {
        // 群聊没有关联案件
        setNoCases(true);
      }
    } catch (error: any) {
      console.error('加载案件列表失败:', error);
      // 所有错误都显示"未关联案件"
      setNoCases(true);
    } finally {
      setLoading(false);
    }
  };

  const loadCaseDetail = async () => {
    if (!selectedCaseId) return;
    
    setCaseLoading(true);
    try {
      const data = await getCaseById(selectedCaseId);
      setCaseData(data);
    } catch (error: any) {
      console.error('加载案件详情失败:', error);
      
      if (error.response?.status === 404) {
        message.error('案件不存在');
      } else if (error.response?.status === 403) {
        message.error('无权限访问此案件');
      } else {
        message.error('加载案件详情失败');
      }
    } finally {
      setCaseLoading(false);
    }
  };

  const handleCaseChange = (caseId: number) => {
    setSelectedCaseId(caseId);
    setActiveTab('followup'); // 切换案件时重置到跟进Tab
  };

  const handleRefresh = () => {
    loadCaseDetail();
  };

  // 加载中状态
  if (loading) {
    return (
      <div className="wechat-case-management-loading">
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#666' }}>加载中...</div>
      </div>
    );
  }

  // 无关联案件状态（包括所有错误情况）
  if (noCases) {
    return (
      <div className="wechat-case-management-empty">
        <Empty 
          description="未关联案件"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // 错误状态（保留用于未来可能的特殊错误处理）
  if (error) {
    return (
      <div className="wechat-case-management-empty">
        <Empty 
          description="未关联案件"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // 无案件状态（兜底，理论上不会到这里）
  if (cases.length === 0) {
    return (
      <div className="wechat-case-management-empty">
        <Empty 
          description="未关联案件"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="wechat-case-management">
      {/* Tab 导航 */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        className="case-management-tabs-header"
      >
        <Tabs.TabPane tab="案件跟进" key="followup" />
        <Tabs.TabPane tab="基础信息" key="info" />
      </Tabs>

      {/* 案件选择器 - 只在跟进Tab显示 */}
      {activeTab === 'followup' && (
        <CaseSelector
          cases={cases}
          selectedCaseId={selectedCaseId}
          onChange={handleCaseChange}
          loading={caseLoading}
          caseData={caseData}
          onRefresh={handleRefresh}
        />
      )}

      {/* Tab 内容区 */}
      {caseData ? (
        <div className="case-management-content">
          {activeTab === 'followup' && (
            <div className="tab-content">
              <CaseFollowUpTab 
                caseId={caseData.id} 
                canEdit={true}
              />
            </div>
          )}
          
          {activeTab === 'info' && (
            <div className="tab-content-no-padding">
              <CaseBasicInfoMobile 
                caseData={caseData}
                canEdit={true}
                onRefresh={handleRefresh}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="case-management-loading">
          <Spin />
        </div>
      )}
    </div>
  );
};

export default CaseManagement;
